/* eslint-disable max-lines */

import { defaultProfileImageUrl } from '../../../features/url/baseUrl';
import { Article } from '../../../types/Article';
import { ButtonFavorite } from '../../Button/ButtonFavorite';
import { RankingIcon } from '../../Icon/RankingIcon';
import { Category } from '../../Tag/Category';
import { Time } from '../../Tag/Time';

import styles from './ArticleCard.module.scss';

import type { FC } from 'react';

import { assertNever, type AppEnv } from '@/packages/nurse-senka-frontend-sdk';

export type ArticleProps = {
  articles: Article[];
  rank: boolean;
  type: 'vertical' | 'horizontal' | 'side';
  appEnv: AppEnv;
};

export type Props = {
  articles: Article[];
  rank: boolean;
  appEnv: AppEnv;
};

const Articles: FC<Props> = ({ articles, rank, appEnv }) => (
  <>
    {articles.map((article) => (
      <a
        className={styles['wrap-article']}
        key={article.id}
        href={article.link}
      >
        <article className={styles['box-article']}>
          <div className={styles['box-thumbnail']}>
            {rank ? (
              <span className={styles['image-rank']}>
                <RankingIcon rankingNumber={article.id} />
              </span>
            ) : (
              ''
            )}
            {article.thumbnailUrl ? (
              <img src={article.thumbnailUrl} alt={article.title} />
            ) : (
              <img src={defaultProfileImageUrl(appEnv)} alt="写真未設定" />
            )}
          </div>
          <div className={styles['box-right']}>
            <div className={styles['text-area']}>
              <div>
                <span className={styles['right-title']}>{article.title}</span>
              </div>

              <div className={styles['right-above']}>
                {article.categories.map((category, index) => (
                  <Category
                    key={index}
                    size="M"
                    color={category.color}
                    text={category.text}
                  />
                ))}
                <Time size="M" text={article.publishedDate} />
              </div>
            </div>

            <div className={styles['favorite-area']}>
              <ButtonFavorite active={article.shouldPressedHeart} />
            </div>
          </div>
        </article>
      </a>
    ))}
  </>
);

// Horizontal
const HorizontalArticles: FC<Props> = ({ articles, rank, appEnv }) => (
  <>
    {articles.map((article) => (
      <a
        className={styles['wrap-horizontal-article']}
        key={article.id}
        href={article.link}
      >
        <article>
          <div className={styles['box-horizontal-thumbnail']}>
            {rank ? (
              <span className={styles['image-rank']}>
                <RankingIcon rankingNumber={article.id} />
              </span>
            ) : (
              ''
            )}
            {article.thumbnailUrl ? (
              <img src={article.thumbnailUrl} alt={article.title} />
            ) : (
              <img src={defaultProfileImageUrl(appEnv)} alt="写真未設定" />
            )}
          </div>
          <div className={styles['box-right']}>
            <div className={styles['text-area']}>
              <div>
                <span className={styles['right-horizontal-title']}>
                  {article.title}
                </span>
              </div>
              <div className={styles['box-right']}>
                <Time size="M" text={article.publishedDate} />
              </div>
            </div>

            <div className={styles['favorite-area']}>
              <ButtonFavorite active={article.shouldPressedHeart} />
            </div>
          </div>
        </article>
      </a>
    ))}
  </>
);

// SideAreaCard
const SideAreaArticles: FC<Props> = ({ articles, rank, appEnv }) => (
  <>
    {articles.map((article) => (
      <a
        className={styles['wrap-article']}
        key={article.id}
        href={article.link}
      >
        <article className={styles['box-article']}>
          <div className={styles['box-side-thumbnail']}>
            {rank ? (
              <span className={styles['image-rank']}>
                <RankingIcon rankingNumber={article.id} />
              </span>
            ) : (
              ''
            )}

            {article.thumbnailUrl ? (
              <img src={article.thumbnailUrl} alt={article.title} />
            ) : (
              <img src={defaultProfileImageUrl(appEnv)} alt="写真未設定" />
            )}
          </div>
          <div className={styles['box-right']}>
            <div className={styles['text-area']}>
              <div>
                <span className={styles['side-right-title']}>
                  {article.title}
                </span>
              </div>

              <div className={styles['box-right']}>
                {article.categories.map((category, index) => (
                  <Category
                    key={index}
                    size="S"
                    color={category.color}
                    text={category.text}
                  />
                ))}
              </div>
            </div>

            <div className={styles['favorite-area']}>
              <ButtonFavorite active={article.shouldPressedHeart} />
            </div>
          </div>
        </article>
      </a>
    ))}
  </>
);

export const ArticleCard: FC<ArticleProps> = ({
  articles,
  rank,
  type,
  appEnv,
}) => {
  switch (type) {
    case 'vertical':
      return (
        <div>
          <Articles rank={rank} appEnv={appEnv} articles={articles} />
        </div>
      );
    case 'horizontal':
      return (
        <div>
          <HorizontalArticles rank={rank} appEnv={appEnv} articles={articles} />
        </div>
      );
    case 'side':
      return (
        <div>
          <SideAreaArticles rank={rank} appEnv={appEnv} articles={articles} />
        </div>
      );
    default:
      return assertNever(type);
  }
};
